import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { applyBrandTheme } from "@/lib/color";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [org, setOrgState] = useState(null);
  const [loading, setLoading] = useState(true);

  const setOrg = useCallback((o) => {
    setOrgState(o);
    applyBrandTheme(o);
  }, []);

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem("nuera_token");
    if (!token) { setLoading(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setOrg(data.org);
    } catch {
      localStorage.removeItem("nuera_token");
    } finally {
      setLoading(false);
    }
  }, [setOrg]);

  useEffect(() => { loadMe(); }, [loadMe]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("nuera_token", data.token);
      setUser(data.user);
      setOrg(data.org);
      return { ok: true, org: data.org };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      localStorage.setItem("nuera_token", data.token);
      setUser(data.user);
      setOrg(data.org);
      return { ok: true, org: data.org };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  };

  const logout = () => {
    localStorage.removeItem("nuera_token");
    setUser(null);
    setOrgState(null);
  };

  const refreshOrg = async () => {
    const { data } = await api.get("/org");
    setOrg(data);
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, org, setOrg, loading, login, register, logout, refreshOrg }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
